import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo, LangSwitch } from "@/components/common";
import { SiteFooter } from "@/components/site";
import { useMeta } from "@/lib/useMeta";

function Shell({ title, children }) {
  const { t } = useI18n();
  const { pathname } = useLocation();
  useMeta(`${title} | woodesing`, t(`meta.auth.${pathname.slice(1)}`), { page: pathname.slice(1) });
  return (
    <>
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col px-6 sm:px-12 py-8">
        <div className="flex items-center justify-between"><Logo /><LangSwitch /></div>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm mx-auto fade-up">
            <h1 className="text-3xl font-semibold mb-8" data-testid="auth-title">{title}</h1>
            {children}
          </div>
        </div>
      </div>
      <div className="hidden lg:block relative bg-[var(--bg-1)] border-l border-[var(--line)] overflow-hidden grain">
        <img src="https://images.unsplash.com/photo-1705326452390-3ecf6070595f?fm=jpg&w=1200&q=80" alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
      </div>
    </div>
    <SiteFooter />
    </>
  );
}

function GoogleButton() {
  const { t } = useI18n();
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button onClick={go} type="button" data-testid="google-signin-btn"
      className="w-full rounded-full bg-white text-neutral-900 font-semibold py-2.5 flex items-center justify-center gap-3 hover:bg-neutral-100 transition-colors">
      <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      {t("auth.google")}
    </button>
  );
}

function Divider() {
  const { t } = useI18n();
  return <div className="flex items-center gap-3 my-6 text-xs text-neutral-500"><span className="h-px bg-[var(--line)] flex-1" />{t("common.or")}<span className="h-px bg-[var(--line)] flex-1" /></div>;
}

function useSubmit(fn) {
  const { errMsg } = useI18n();
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await fn(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return [busy, run];
}

const SubmitBtn = ({ busy, children, testid }) => (
  <button disabled={busy} className="btn-brand w-full rounded-full py-2.5 font-semibold disabled:opacity-60" data-testid={testid}>{children}</button>
);

export function LoginPage() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, submit] = useSubmit(async () => {
    const { data } = await api.post("/auth/login", f);
    setUser(data);
    nav(loc.state?.from || (data.role === "admin" ? "/admin" : "/app"));
  });
  if (user) return <Navigate to="/app" replace />;
  return (
    <Shell title={t("auth.loginTitle")}>
      <GoogleButton />
      <Divider />
      <form onSubmit={submit} className="space-y-4">
        <div><Label htmlFor="email">{t("common.email")}</Label><Input id="email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" className="mt-1.5" /></div>
        <div><Label htmlFor="pw">{t("common.password")}</Label><Input id="pw" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" className="mt-1.5" /></div>
        <SubmitBtn busy={busy} testid="login-submit-btn">{t("common.signIn")}</SubmitBtn>
      </form>
      <div className="mt-6 flex justify-between text-sm">
        <Link to="/forgot-password" className="text-neutral-400 hover:text-white" data-testid="forgot-link">{t("auth.forgot")}</Link>
        <Link to="/register" className="text-emerald-400 hover:underline" data-testid="to-register-link">{t("common.signUp")}</Link>
      </div>
    </Shell>
  );
}

export function RegisterPage() {
  const { t, lang } = useI18n();
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, submit] = useSubmit(async () => {
    const { data } = await api.post("/auth/register", { ...f, lang });
    setUser(data);
    nav("/app");
  });
  if (user) return <Navigate to="/app" replace />;
  return (
    <Shell title={t("auth.registerTitle")}>
      <GoogleButton />
      <Divider />
      <form onSubmit={submit} className="space-y-4">
        <div><Label htmlFor="name">{t("common.name")}</Label><Input id="name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="register-name-input" className="mt-1.5" /></div>
        <div><Label htmlFor="remail">{t("common.email")}</Label><Input id="remail" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="register-email-input" className="mt-1.5" /></div>
        <div><Label htmlFor="rpw">{t("common.password")}</Label><Input id="rpw" type="password" minLength={8} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="register-password-input" className="mt-1.5" />
          <p className="text-xs text-neutral-500 mt-1">{t("auth.pwHint")}</p></div>
        <SubmitBtn busy={busy} testid="register-submit-btn">{t("common.signUp")}</SubmitBtn>
        <p className="text-xs text-neutral-500">{t("auth.terms")}</p>
      </form>
      <p className="mt-6 text-sm text-neutral-400">{t("auth.haveAccount")} <Link to="/login" className="text-emerald-400 hover:underline" data-testid="to-login-link">{t("common.signIn")}</Link></p>
    </Shell>
  );
}

export function ForgotPage() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, submit] = useSubmit(async () => {
    await api.post("/auth/forgot-password", { email });
    setSent(true);
  });
  return (
    <Shell title={t("auth.forgotTitle")}>
      {sent ? (
        <div className="space-y-3" data-testid="forgot-sent"><p>{t("auth.forgotSent")}</p><p className="text-xs text-neutral-400">{t("auth.forgotNote")}</p></div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label htmlFor="femail">{t("common.email")}</Label><Input id="femail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" className="mt-1.5" /></div>
          <SubmitBtn busy={busy} testid="forgot-submit-btn">{t("auth.sendLink")}</SubmitBtn>
        </form>
      )}
      <Link to="/login" className="block mt-6 text-sm text-neutral-400 hover:text-white">{t("common.back")}</Link>
    </Shell>
  );
}

export function VerifyEmailPage() {
  const { t } = useI18n();
  const { user, refreshMe } = useAuth();
  const [params] = useSearchParams();
  const [state, setState] = useState("pending");
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    api.post("/auth/verify-email", { token: params.get("token") || "" })
      .then(() => { setState("ok"); refreshMe(); })
      .catch(() => setState("fail"));
  }, [params, refreshMe]);
  return (
    <Shell title={t("auth.verifyTitle")}>
      <p data-testid="verify-email-status" className={state === "fail" ? "text-red-400" : state === "ok" ? "text-emerald-400" : "text-neutral-400"}>
        {t(state === "ok" ? "auth.verifyOk" : state === "fail" ? "auth.verifyFail" : "auth.verifying")}
      </p>
      <Link to={user ? "/app" : "/login"} className="btn-brand inline-flex mt-8 rounded-full px-6 py-2.5 font-semibold" data-testid="verify-continue-btn">
        {user ? t("auth.goApp") : t("common.signIn")}
      </Link>
    </Shell>
  );
}

export function ResetPage() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [busy, submit] = useSubmit(async () => {
    await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw });
    toast.success(t("auth.resetDone"));
    nav("/login");
  });
  return (
    <Shell title={t("auth.resetTitle")}>
      <form onSubmit={submit} className="space-y-4">
        <div><Label htmlFor="npw">{t("auth.newPassword")}</Label><Input id="npw" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" className="mt-1.5" /></div>
        <SubmitBtn busy={busy} testid="reset-submit-btn">{t("common.save")}</SubmitBtn>
      </form>
    </Shell>
  );
}
